/**
 * WhatyLive 客户端 SDK：WebSocket 信令 + 事件分发 + 自动重连。
 *
 * Web 端与 Tauri 桌面端共用本文件。媒体部分见 media.ts。
 */

import { BIN_FRAME_VERSION, MsgType, type Envelope, type ProtoError } from './messages'

/** 连接状态。 */
export type ConnState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed'

/** 活性检测的节拍。三个值的关系：检查间隔 < 发 ping 的静默阈值 < 判死阈值。 */
const ALIVE_CHECK_MS = 5_000
/** 静默超过它就主动 ping 一下探活。服务端 25 秒会主动发一次，
 *  所以正常情况下根本轮不到这里。 */
const ALIVE_PING_MS = 20_000
/** 静默超过它就认定连接已死。取值要大于服务端的 ping 周期（25 秒）
 *  加上一次往返，否则网络稍抖一下就会误判成掉线、白白重连一次。 */
const ALIVE_TIMEOUT_MS = 40_000

/** 事件监听器。 */
type Listener = (data: unknown, env: Envelope) => void

export interface ClientOptions {
  /** WebSocket 地址，如 wss://live.example.edu/ws；传函数则每次（重）连都重新解析 —— 线路切换靠它 */
  url: string | (() => string)
  /** 登录令牌 */
  token: string
  /** 客户端版本标识 */
  clientVer?: string
  /** 重连的最大退避（毫秒），默认 15 秒 */
  maxBackoffMs?: number
  /** 关闭自动重连 */
  noReconnect?: boolean
  /**
   * 同一条线路连续失败 FAILOVER_AFTER 次后调用，让上层换一条线路（多服务器备份）。
   * 返回 true 表示已换线，退避计数清零、立刻重连；false 表示没得换，继续在原线路退避。
   */
  onFailover?: () => Promise<boolean>
}

/** 连续失败几次才切线路。太小会把一次网络抖动当成服务器挂了，来回横跳。 */
export const FAILOVER_AFTER = 2

/**
 * Client 管理与服务端的一条信令连接。
 *
 * 断线重连是刻意做进 SDK 的：老系统的 ActiveX 客户端断线后只弹一个
 * 「网络错误」对话框让用户自己重进，课上掉一次线就得重新登录进房。
 */
export class Client {
  private opt: Required<Pick<ClientOptions, 'url' | 'token'>> & ClientOptions
  private ws: WebSocket | null = null

  /** 活性检测用：最近一次收到任何消息的时刻。 */
  private lastSeen = 0
  private aliveTimer: number | null = null
  private listeners = new Map<string, Set<Listener>>()
  private seq = 1
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: number }>()

  private _state: ConnState = 'idle'
  private reconnectAttempt = 0
  private reconnectTimer: number | null = null
  private closedByUser = false

  /** 进房参数。重连后用它自动回到原房间。 */
  private rejoin: { roomId: number; password?: string; nick?: string; info?: string } | null = null

  constructor(options: ClientOptions) {
    this.opt = { clientVer: 'web/0.1.0', maxBackoffMs: 15000, ...options }
  }

  get state(): ConnState {
    return this._state
  }

  /** 订阅某类型消息，返回退订函数。 */
  on<T = unknown>(type: string, fn: (data: T, env: Envelope) => void): () => void {
    let set = this.listeners.get(type)
    if (!set) {
      set = new Set()
      this.listeners.set(type, set)
    }
    set.add(fn as Listener)
    return () => set!.delete(fn as Listener)
  }

  /** 建立连接并完成握手。 */
  async connect(): Promise<void> {
    this.closedByUser = false
    await this.openSocket()
  }

  /** 主动断开，不再重连。 */
  close(): void {
    this.closedByUser = true
    this.clearReconnect()
    this.stopAliveCheck()
    this.setState('closed')
    this.ws?.close(1000, 'bye')
    this.ws = null
    for (const [, p] of this.pending) {
      window.clearTimeout(p.timer)
      p.reject(new Error('连接已关闭'))
    }
    this.pending.clear()
  }

  /** 发送一条消息，不等待应答。 */
  send(type: string, data?: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    const env: Envelope = { t: type }
    if (data !== undefined) env.d = data
    this.ws.send(JSON.stringify(env))
  }

  /**
   * 发送并等待应答。
   * expectType 是期望的应答类型；收到 error 则以异常返回。
   */
  request<T = unknown>(type: string, data: unknown, expectType: string, timeoutMs = 10000): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (this.ws?.readyState !== WebSocket.OPEN) {
        reject(new Error('尚未连接'))
        return
      }
      const i = this.seq++
      const timer = window.setTimeout(() => {
        this.pending.delete(i)
        reject(new Error(`${type} 请求超时`))
      }, timeoutMs)

      this.pending.set(i, {
        resolve: resolve as (v: unknown) => void,
        reject,
        timer,
      })

      const env: Envelope = { t: type, i }
      if (data !== undefined) env.d = data
      this.ws.send(JSON.stringify(env))
      void expectType
    })
  }

  /** 发送二进制帧。帧格式见 docs/03-protocol.md 第 2 节。 */
  sendBinary(kind: number, header: unknown, payload: Uint8Array): boolean {
    // 返回是否真的发出去了。连接没开时静默丢弃会让分片上传「成功」地丢掉一半，
    // 调用方据此报错，而不是让文件停在 99% 永远传不完。
    if (this.ws?.readyState !== WebSocket.OPEN) return false
    const hdr = new TextEncoder().encode(JSON.stringify(header))
    const buf = new Uint8Array(4 + hdr.length + payload.length)
    const view = new DataView(buf.buffer)
    buf[0] = BIN_FRAME_VERSION
    buf[1] = kind
    view.setUint16(2, hdr.length, true)
    buf.set(hdr, 4)
    buf.set(payload, 4 + hdr.length)
    this.ws.send(buf)
    return true
  }

  /** 记住进房参数，供重连后自动回房。 */
  rememberRoom(p: { roomId: number; password?: string; nick?: string; info?: string }): void {
    this.rejoin = p
  }

  // ---------- 内部实现 ----------

  private openSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.setState(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting')

      let ws: WebSocket
      try {
        ws = new WebSocket(typeof this.opt.url === 'function' ? this.opt.url() : this.opt.url)
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)))
        return
      }
      ws.binaryType = 'arraybuffer'
      this.ws = ws

      let settled = false

      ws.onopen = () => {
        // 握手：发 hello，等 hello.ok。
        this.send(MsgType.Hello, { token: this.opt.token, clientVer: this.opt.clientVer })
      }

      const offOK = this.on(MsgType.HelloOK, () => {
        // 只认本条 socket 的握手应答：旧 socket 被活性检测接管后不会再走 onclose，
        // 靠这一句兜底，别让它的监听在下一条连接上被触发。
        if (this.ws !== ws) {
          offOK()
          return
        }
        offOK()
        this.reconnectAttempt = 0
        this.setState('connected')
        if (!settled) {
          settled = true
          resolve()
        }
        this.startAliveCheck()
        // 重连成功后自动回到原房间。
        if (this.rejoin) {
          this.send(MsgType.RoomJoin, this.rejoin)
        }
      })

      ws.onmessage = (ev) => {
        this.lastSeen = Date.now()
        this.onMessage(ev)
      }

      ws.onerror = () => {
        // 这条 socket 已经失败，它的 hello.ok 监听必须一并摘掉。
        // 监听挂在 Client 上而不是 socket 上，不摘的话每失败一次就多留一个：
        // 连续失败三次后连上，四个监听都会触发，于是发出四条 room.join。
        offOK()
        if (!settled) {
          settled = true
          reject(new Error('连接失败'))
        }
      }

      ws.onclose = () => {
        offOK()
        this.stopAliveCheck()
        this.ws = null
        if (!settled) {
          settled = true
          reject(new Error('连接已关闭'))
        }
        this.emit('__disconnected', null, { t: '__disconnected' })
        if (!this.closedByUser && !this.opt.noReconnect) {
          this.scheduleReconnect()
        } else {
          this.setState('closed')
        }
      }
    })
  }

  private onMessage(ev: MessageEvent): void {
    if (typeof ev.data !== 'string') {
      this.onBinary(new Uint8Array(ev.data as ArrayBuffer))
      return
    }
    let env: Envelope
    try {
      env = JSON.parse(ev.data) as Envelope
    } catch {
      return
    }

    // 有序号的应答优先交给等待方。
    if (env.i !== undefined && this.pending.has(env.i)) {
      const p = this.pending.get(env.i)!
      this.pending.delete(env.i)
      window.clearTimeout(p.timer)
      if (env.t === MsgType.Error) {
        const e = env.d as ProtoError
        p.reject(new ClientError(e?.code ?? 'INTERNAL', e?.msg ?? '未知错误'))
      } else {
        p.resolve(env.d)
      }
      // 应答同时也广播出去，便于界面统一处理。
    }

    this.emit(env.t, env.d, env)
  }

  private onBinary(buf: Uint8Array): void {
    if (buf.length < 4 || buf[0] !== BIN_FRAME_VERSION) return
    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength)
    const kind = buf[1]
    const hdrLen = view.getUint16(2, true)
    if (buf.length < 4 + hdrLen) return
    let header: unknown = null
    try {
      header = JSON.parse(new TextDecoder().decode(buf.subarray(4, 4 + hdrLen)))
    } catch {
      return
    }
    this.emit('__binary', { kind, header, payload: buf.subarray(4 + hdrLen) }, { t: '__binary' })
  }

  private emit(type: string, data: unknown, env: Envelope): void {
    const set = this.listeners.get(type)
    if (!set) return
    for (const fn of set) {
      try {
        fn(data, env)
      } catch (e) {
        console.error(`[whatylive] 处理 ${type} 时出错`, e)
      }
    }
  }

  /**
   * 自己盯着连接还活着没有。
   *
   * 光靠 ws.onclose 是不够的：网络断在中间时（移动网络切换、NAT 表项超时、
   * 拔网线），TCP 连接会变成「半开」—— 数据一个字节都过不去，可 onclose
   * 迟迟不来。实测把网络切断后两分钟，界面上还显示「已连接」，
   * 而学生在那两分钟里说的话、画的板书，一样都没发出去。
   *
   * 服务端每 25 秒会 ping 一次，但浏览器把协议层的 ping/pong 处理掉了，
   * 不会通知 JS。所以这里发应用层的 ping：服务端收到会回 pong
   * （见 signal/dispatch.go），只要连接是通的，lastSeen 就会被刷新。
   */
  private startAliveCheck(): void {
    this.stopAliveCheck()
    this.lastSeen = Date.now()
    this.aliveTimer = window.setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
      const silent = Date.now() - this.lastSeen
      if (silent > ALIVE_TIMEOUT_MS) {
        // 认定连接已死。
        //
        // 只调 close() 是不够的：网络不通时关闭握手也发不出去，
        // socket 会停在 CLOSING 一直不动，onclose 永远不来，
        // 于是重连也永远不会开始 —— 表现就是界面卡在「已连接」，
        // 而实际上早就断了。所以这里主动接管：解绑旧 socket，
        // 自己触发一次断开流程。
        const dead = this.ws
        this.ws = null
        this.stopAliveCheck()
        dead.onopen = null
        dead.onmessage = null
        dead.onerror = null
        dead.onclose = null
        try {
          dead.close(4000, 'alive-timeout')
        } catch {
          // 已经处于关闭中，忽略。
        }
        this.emit('__disconnected', null, { t: '__disconnected' })
        if (!this.closedByUser && !this.opt.noReconnect) {
          this.scheduleReconnect()
        } else {
          this.setState('closed')
        }
        return
      }
      if (silent > ALIVE_PING_MS) {
        this.send(MsgType.Ping, {})
      }
    }, ALIVE_CHECK_MS)
  }

  private stopAliveCheck(): void {
    if (this.aliveTimer !== null) {
      window.clearInterval(this.aliveTimer)
      this.aliveTimer = null
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnect()
    this.setState('reconnecting')
    // 指数退避 + 抖动，避免服务端重启时所有客户端同时涌回来。
    const base = Math.min(1000 * 2 ** this.reconnectAttempt, this.opt.maxBackoffMs!)
    const delay = base * (0.5 + Math.random() * 0.5)
    this.reconnectAttempt++
    this.reconnectTimer = window.setTimeout(() => {
      void this.maybeFailover().then(() => {
        // 问换线是异步的，这期间上层可能已经 close() 了（例如重新进房换了新 client），
        // 那就不能再开 socket —— 否则一个已经废弃的 client 会在后台一直重连。
        if (this.closedByUser) return
        return this.openSocket().catch(() => {
          /* 失败会再次触发 onclose，继续退避 */
        })
      })
    }, delay)
  }

  /** 这条线路已经连续失败够多次：问上层要不要换线。换成功就从头计数。 */
  private async maybeFailover(): Promise<void> {
    if (this.reconnectAttempt < FAILOVER_AFTER || !this.opt.onFailover) return
    try {
      if (await this.opt.onFailover()) this.reconnectAttempt = 0
    } catch {
      /* 换线本身失败就当没换，继续原线路退避 */
    }
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private setState(s: ConnState): void {
    if (this._state === s) return
    this._state = s
    this.emit('__state', s, { t: '__state' })
  }
}

/** 携带错误码的客户端异常。 */
export class ClientError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ClientError'
  }
}
